import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Skeleton } from "@/components/kit";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import type { CustomerGeoResultDTO } from "@/modules/reports/domain/types";
import { formatLak } from "@/shared/lib/currency";

const chartConfig = {
  revenue: {
    label: "ຍອດຂາຍ",
    color: "var(--chart-1)",
  },
} satisfies ChartConfig;

type CustomerGeoChartProps = {
  result?: CustomerGeoResultDTO;
  isLoading?: boolean;
  limit?: number;
};

export function CustomerGeoChart({
  result,
  isLoading,
  limit = 8,
}: CustomerGeoChartProps) {
  if (isLoading) {
    return <Skeleton className="h-[240px] w-full rounded-2xl" />;
  }

  const rows = (result?.data ?? [])
    .filter((row) => row.label !== "ບໍ່ລະບຸ")
    .slice(0, limit);

  if (rows.length === 0) {
    return (
      <div className="brand-card flex h-[240px] items-center justify-center p-6 text-muted-foreground text-sm">
        ບໍ່ມີຂໍ້ມູນໃນຊ່ວງນີ້
      </div>
    );
  }

  const data = rows.map((row) => ({
    label:
      row.label.length > 14 ? `${row.label.slice(0, 14)}…` : row.label,
    fullLabel: row.label,
    revenue: Number(row.revenue),
    customerCount: row.customerCount,
  }));

  return (
    <div className="brand-card min-w-0 overflow-hidden p-4 sm:p-5">
      <h2 className="mb-4 font-display text-lg">Top ແຂວງຕາມຍອດ</h2>
      <ChartContainer
        config={chartConfig}
        className="aspect-auto h-[220px] w-full min-w-0"
      >
        <BarChart
          data={data}
          layout="vertical"
          margin={{ left: 4, right: 12, top: 8, bottom: 0 }}
        >
          <CartesianGrid horizontal={false} />
          <XAxis type="number" tickLine={false} axisLine={false} hide />
          <YAxis
            type="category"
            dataKey="label"
            width={88}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11 }}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                formatter={(value, _name, item) => (
                  <span className="font-medium tabular-nums">
                    {formatLak(String(value))} · {item.payload.customerCount}{" "}
                    ລູກຄ້າ
                  </span>
                )}
                labelFormatter={(_label, payload) =>
                  payload?.[0]?.payload?.fullLabel ?? _label
                }
              />
            }
          />
          <Bar
            dataKey="revenue"
            fill="var(--color-revenue)"
            radius={[0, 4, 4, 0]}
          />
        </BarChart>
      </ChartContainer>
    </div>
  );
}
